import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GetStartedButton } from '../common/GetStartedButton';
import {
  PROBLEM,
  REAL_COST,
  WHAT_HASNT_WORKED,
  BRIDGE,
  HOW_IT_WORKS,
  FEATURES,
  TRANSFORMATION,
  FAQ,
  URGENCY,
  CLOSING_CTA,
} from './homepageCopy';

// Homepage sections built from the original landing page copy, styled with the
// same eyebrows, headings, cards, and navy bands as the rest of the homepage.

const SECTION_PADDING = 'px-4 sm:px-6 lg:px-8 py-16 sm:py-20';
const EYEBROW = 'text-jp-muted dark:text-jp-gold text-xs font-bold uppercase tracking-widest';
const CARD = 'bg-jp-card rounded-2xl border border-jp-outline/70 shadow-sm p-4';
const TILE = 'rounded-xl p-5 bg-jp-section border border-jp-outline/50';

const SectionHeader = ({ eyebrow, title, accent, subtitle }) => (
  <div className="mb-6 max-w-3xl">
    {eyebrow && <p className={`${EYEBROW} mb-2`}>{eyebrow}</p>}
    <h2 className="text-2xl sm:text-3xl font-extrabold text-jp-ink">
      {title} <span className="text-jp-accent">{accent}</span>
    </h2>
    {subtitle && <p className="text-jp-muted text-sm sm:text-base leading-relaxed mt-2">{subtitle}</p>}
  </div>
);

const TileGrid = ({ items, columns = 'sm:grid-cols-2 lg:grid-cols-3', tileClassName = () => '', renderTile }) => (
  <div className={`grid grid-cols-1 ${columns} gap-4 ${CARD}`}>
    {items.map((item, index) => (
      <div key={item.title || item.label || item.lead} className={`${TILE} ${tileClassName(index)}`}>
        {renderTile(item, index)}
      </div>
    ))}
  </div>
);

const TileTitle = ({ children }) => <h3 className="text-lg font-bold text-jp-ink mb-2">{children}</h3>;
const TileText = ({ children }) => <p className="text-jp-muted text-sm leading-relaxed">{children}</p>;

// Navy panel shared with the newsletter signup; white text in both themes.
const Band = ({ children }) => (
  <section className={`bg-jp-page ${SECTION_PADDING}`}>
    <div className="max-w-7xl mx-auto rounded-2xl bg-jp-band border border-jp-gold/40 px-6 py-8 sm:px-10 sm:py-10">
      {children}
    </div>
  </section>
);

// Both columns of the problem bubble share these sizes so they read as one block.
const PROBLEM_HEADING = 'text-xl sm:text-2xl font-extrabold text-jp-ink mb-4';
const PROBLEM_BODY = 'text-jp-muted text-sm sm:text-base leading-relaxed';

export const ProblemStorySection = () => (
  <section className={`bg-jp-page ${SECTION_PADDING}`}>
    {/* One bubble holds both the story and the real-cost list */}
    <div className={`max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 ${CARD} sm:p-8`}>
      <div>
        <p className={`${EYEBROW} mb-2`}>{PROBLEM.eyebrow}</p>
        <h2 className={PROBLEM_HEADING}>
          {PROBLEM.title} <span className="text-jp-accent">{PROBLEM.accent}</span>
        </h2>
        <div className={`space-y-3 ${PROBLEM_BODY}`}>
          <p className="text-jp-ink font-bold">{PROBLEM.lead}</p>
          {PROBLEM.paragraphs.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
          <p className="text-jp-ink font-bold">{PROBLEM.closer}</p>
        </div>
      </div>

      <div className="border-t border-jp-line pt-8 lg:border-t-0 lg:pt-0 lg:border-l lg:pl-10">
        <p className={`${EYEBROW} mb-2`}>{REAL_COST.eyebrow}</p>
        <h3 className={PROBLEM_HEADING}>
          {REAL_COST.title} <span className="text-jp-accent">{REAL_COST.accent}</span>
        </h3>
        <ul className="space-y-3">
          {REAL_COST.items.map((item) => (
            <li key={item.lead} className={`flex gap-3 ${PROBLEM_BODY}`}>
              <span aria-hidden="true" className="mt-[0.6em] w-1.5 h-1.5 flex-shrink-0 rounded-full bg-jp-accent"></span>
              <span>
                <span className="text-jp-ink font-bold">{item.lead}</span> {item.text}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </section>
);

export const WhatHasntWorkedSection = () => (
  <section className="bg-jp-section border-y border-jp-line px-4 sm:px-6 lg:px-8 py-12 sm:py-14">
    <div className="max-w-7xl mx-auto">
      <SectionHeader {...WHAT_HASNT_WORKED} />
      {/* Compact tiles; six-column grid keeps the two-tile bottom row centered */}
      <TileGrid
        items={WHAT_HASNT_WORKED.items}
        columns="sm:grid-cols-2 lg:grid-cols-6"
        tileClassName={(index) => `!p-4 lg:col-span-2 ${index === 3 && WHAT_HASNT_WORKED.items.length === 5 ? 'lg:col-start-2' : ''}`}
        renderTile={(item) => (
          <>
            <h3 className="text-base font-bold text-jp-ink mb-1">{item.label}</h3>
            <TileText>“{item.quote}”</TileText>
          </>
        )}
      />
    </div>
  </section>
);

export const BridgeSection = () => {
  const navigate = useNavigate();

  return (
    <Band>
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.3fr] gap-8 lg:gap-12">
        <div className="flex flex-col items-start">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-4">
            {BRIDGE.title} <span className="text-cyan-400">{BRIDGE.accent}</span>
          </h2>
          <p className="text-xl sm:text-2xl font-extrabold text-white mb-6">
            {BRIDGE.tagline} <span className="text-cyan-400">{BRIDGE.brand}</span>
          </p>
          <GetStartedButton onClick={() => navigate('/auth/signup')}>START YOUR SEARCH</GetStartedButton>
        </div>
        <div className="space-y-4 text-slate-300 text-sm sm:text-base leading-relaxed">
          {BRIDGE.paragraphs.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
          <p className="text-yellow-300 font-semibold">{BRIDGE.highlight}</p>
          {BRIDGE.closing.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
        </div>
      </div>
    </Band>
  );
};

export const HowItWorksSection = () => (
  <section className={`bg-jp-section border-y border-jp-line ${SECTION_PADDING}`}>
    <div className="max-w-7xl mx-auto">
      <SectionHeader {...HOW_IT_WORKS} />
      <TileGrid
        items={HOW_IT_WORKS.steps}
        columns="md:grid-cols-3"
        renderTile={(step, index) => (
          <>
            <div className="w-11 h-11 rounded-full bg-jp-accent/15 flex items-center justify-center mb-4 text-jp-accent-ink font-extrabold">
              {index + 1}
            </div>
            <TileTitle>{step.title}</TileTitle>
            <TileText>{step.text}</TileText>
          </>
        )}
      />
    </div>
  </section>
);

export const FeaturesSection = () => (
  <section className={`bg-jp-page ${SECTION_PADDING}`}>
    <div className="max-w-7xl mx-auto">
      <SectionHeader {...FEATURES} />
      {/* Six-column grid with two-column tiles so a short bottom row sits centered */}
      <TileGrid
        items={FEATURES.items}
        columns="sm:grid-cols-2 lg:grid-cols-6"
        tileClassName={(index) => `lg:col-span-2 ${index === 3 && FEATURES.items.length === 5 ? 'lg:col-start-2' : ''}`}
        renderTile={(feature) => (
          <>
            <div className="flex items-start justify-between gap-3">
              <TileTitle>{feature.title}</TileTitle>
              {feature.comingSoon && (
                <span className="flex-shrink-0 text-[10px] font-semibold uppercase tracking-wider text-jp-muted border border-jp-line rounded-full px-3 py-1">
                  Coming Soon
                </span>
              )}
            </div>
            <TileText>{feature.text}</TileText>
          </>
        )}
      />
    </div>
  </section>
);

const TRANSFORMATION_ITEMS = TRANSFORMATION.items.filter((item) => !item.hidden);

export const TransformationSection = () => (
  <section className={`bg-jp-page border-b border-jp-line ${SECTION_PADDING}`}>
    <div className="max-w-7xl mx-auto">
      <SectionHeader {...TRANSFORMATION} />
      {/* Same centered bottom row as the features grid */}
      <TileGrid
        items={TRANSFORMATION_ITEMS}
        columns="sm:grid-cols-2 lg:grid-cols-6"
        tileClassName={(index) => `lg:col-span-2 ${index === 3 && TRANSFORMATION_ITEMS.length === 5 ? 'lg:col-start-2' : ''}`}
        renderTile={(item) => (
          <>
            <TileTitle>{item.title}</TileTitle>
            <TileText>{item.text}</TileText>
          </>
        )}
      />
    </div>
  </section>
);

export const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <section className={`bg-jp-section border-y border-jp-line ${SECTION_PADDING}`}>
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-6 lg:gap-10">
        <SectionHeader {...FAQ} />
        <div className={`${CARD} divide-y divide-jp-line`}>
          {FAQ.items.map((item, index) => {
            const isOpen = openIndex === index;
            return (
              <div key={item.question}>
                <button
                  type="button"
                  onClick={() => setOpenIndex(isOpen ? null : index)}
                  aria-expanded={isOpen}
                  aria-controls={`faq-answer-${index}`}
                  className="w-full flex items-center justify-between gap-4 px-2 py-4 text-left rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-jp-accent"
                >
                  <span className={`font-bold transition-colors duration-300 ${isOpen ? 'text-jp-accent-ink' : 'text-jp-ink'}`}>
                    {item.question}
                  </span>
                  <svg
                    className={`w-5 h-5 flex-shrink-0 text-jp-accent transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {isOpen && (
                  <p id={`faq-answer-${index}`} className="px-2 pb-4 text-jp-muted text-sm leading-relaxed">
                    {item.answer}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export const UrgencySection = () => {
  const navigate = useNavigate();

  return (
    <Band>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
        <div>
          <p className="text-yellow-300 text-xs font-bold uppercase tracking-widest mb-2">{URGENCY.eyebrow}</p>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-4">
            {URGENCY.title} <span className="text-cyan-400">{URGENCY.accent}</span>
          </h2>
          <div className="space-y-3 text-slate-300 text-sm sm:text-base leading-relaxed">
            {URGENCY.paragraphs.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
            <p className="text-yellow-300 font-semibold">{URGENCY.closer}</p>
          </div>
        </div>
        <div className="flex flex-col items-start lg:border-l lg:border-white/10 lg:pl-12">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-4">
            {CLOSING_CTA.title} <span className="text-cyan-400">{CLOSING_CTA.accent}</span>
          </h2>
          <p className="text-slate-300 text-sm sm:text-base leading-relaxed mb-6">{CLOSING_CTA.text}</p>
          <GetStartedButton onClick={() => navigate('/auth/signup')}>{CLOSING_CTA.button}</GetStartedButton>
          <p className="text-slate-400 text-xs mt-4">{CLOSING_CTA.note}</p>
        </div>
      </div>
    </Band>
  );
};
